/**
 * Local fetch-based service for the XEPPT Connect popup endpoints.
 * Does NOT use the xeppt-sdk — these endpoints are not part of the published SDK.
 * Replicates the SDK's auth: a Bearer access token from the 'client-metadata'
 * localStorage entry (set by the SDK on login). The backend authenticates via the
 * Authorization header. We do NOT send credentials:'include': the backend answers
 * Access-Control-Allow-Origin '*' with Allow-Credentials false, and browsers block a
 * credentialed cross-origin request under those CORS headers. Bearer needs no cookies.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

// The XEPPT SDK authenticates with a Bearer access token (stored by the SDK in the
// 'client-metadata' localStorage entry on login), NOT a cookie. These local fetches
// must attach the same token or the backend returns 401 (rendered as "session expired").
function authHeaders(): Record<string, string> {
    try {
        const raw = localStorage.getItem('client-metadata');
        if (!raw) return {};
        const meta = JSON.parse(raw) as { accessToken?: string };
        return meta?.accessToken ? { Authorization: `Bearer ${meta.accessToken}` } : {};
    } catch {
        return {};
    }
}

export interface ConnectSession {
    merchant_name: string;
    scope: string;
    redirect_origin: string;
    // Payment session fields — null for identity-only sessions.
    amount_cents?: number | null;
    currency?: string;
    subtotal_cents?: number;
    shipping_cents?: number;
    fee_cents?: number;
    line_items?: { name: string; description: string; quantity: number; unit_price_cents: number }[] | null;
    expires_at?: string;
    // Consumer's spendable wallet balance (balance minus reserved), in the
    // session's currency, minor units. Used to gate the "Complete payment" button.
    wallet_balance_cents: number;
    // Consumer's display identity, resolved server-side — authoritative source
    // for the popup avatar/initials (unlike the best-effort /profile fetch).
    first_name: string;
    last_name: string;
    avatar: string | null;
}

export interface ConnectConsentResponse {
    code: string;
}

export interface ConnectChargeResult {
    transaction_id: string;
    currency: string;
    amount: number;
    status: string;
    // Anonymous, stable, per-merchant customer id (scoped_id) for the payer.
    customer_id?: string;
    // The paying customer's XEPPT @handle (username), when they have one.
    username?: string;
}

export class ConnectApiError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly code?: string,
        options?: ErrorOptions
    ) {
        super(message, options);
        this.name = 'ConnectApiError';
    }
}

async function parseResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        throw new ConnectApiError(`HTTP ${response.status}`, response.status);
    }
    return response.json() as Promise<T>;
}

export const apiConnectService = {
    async getSession(token: string): Promise<ConnectSession> {
        const response = await fetch(
            `${BACKEND_URL}/connect/session?token=${encodeURIComponent(token)}`,
            { headers: authHeaders() }
        );
        return parseResponse<ConnectSession>(response);
    },

    async consent(token: string): Promise<ConnectConsentResponse> {
        const response = await fetch(`${BACKEND_URL}/connect/consent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ connect_token: token })
        });
        return parseResponse<ConnectConsentResponse>(response);
    },

    // Bearer token is attached via authHeaders(). NO credentials option is set —
    // the backend serves Access-Control-Allow-Origin '*' without credentials, and
    // browsers reject credentialed requests under that CORS policy.
    async charge(token: string): Promise<ConnectChargeResult> {
        const response = await fetch(`${BACKEND_URL}/connect/charge`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ connect_token: token })
        });
        if (!response.ok) {
            // Parse the error body to extract the machine-readable code (e.g. INSUFFICIENT_FUNDS).
            let code: string | undefined;
            try {
                const body = await response.json() as { code?: string };
                code = typeof body?.code === 'string' ? body.code : undefined;
            } catch {
                // Ignore JSON parse failures on error bodies.
            }
            throw new ConnectApiError(`HTTP ${response.status}`, response.status, code);
        }
        return response.json() as Promise<ConnectChargeResult>;
    }
};
